import cookie from 'cookie'
import * as moment from 'moment'
import { DEFAULT_SEARCH_FILTER_GUESTS } from '../static/constants'
import { IFeature } from 'static/interfaces'
import { ISelectedGuests } from 'utils/reservation'

export interface ISearchFiltersCookieValue {
  areaFeature: Pick<IFeature, 'id' | 'slug' | 'title'> | null
  date: string | null
  dateRange: {
    startDate: string
    endDate: string
  } | null
  guests: ISelectedGuests
}

const SEARCH_FILTERS_COOKIE_KEY = 'search_filters'
const SEARCH_FILTERS_COOKIE_MAX_AGE = 60 * 60 * 24 * 7

const getValidNumberOrDefault = (value: unknown, defaultValue: number): number => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return defaultValue
  }

  return value
}

// 原則ISelectedGuestsではあるが、cookieから取得した値を考慮
export const normalizeSearchFilterGuests = (guests: unknown): ISelectedGuests => {
  const rawGuests = (guests && typeof guests === 'object' ? guests : {}) as Partial<ISelectedGuests>
  const adults = getValidNumberOrDefault(rawGuests.adults, DEFAULT_SEARCH_FILTER_GUESTS.adults)
  const children = getValidNumberOrDefault(
    rawGuests.children,
    DEFAULT_SEARCH_FILTER_GUESTS.children
  )
  const infants = getValidNumberOrDefault(rawGuests.infants, DEFAULT_SEARCH_FILTER_GUESTS.infants)

  return {
    adults: Math.max(1, adults),
    children: Math.max(0, children),
    infants: Math.max(0, infants),
  }
}

export const getSearchFiltersCookie = (): ISearchFiltersCookieValue | null => {
  if (typeof document === 'undefined') {
    return null
  }

  const parsedCookie = cookie.parse(document.cookie)[SEARCH_FILTERS_COOKIE_KEY]
  if (!parsedCookie) {
    return null
  }

  try {
    const decodedCookie = JSON.parse(parsedCookie) as Partial<ISearchFiltersCookieValue>
    const hasDateRange =
      decodedCookie.dateRange &&
      typeof decodedCookie.dateRange === 'object' &&
      'startDate' in decodedCookie.dateRange &&
      'endDate' in decodedCookie.dateRange
    const hasValidDateRange =
      hasDateRange &&
      typeof decodedCookie.dateRange.startDate === 'string' &&
      typeof decodedCookie.dateRange.endDate === 'string'

    return {
      areaFeature: decodedCookie.areaFeature ?? null,
      date: typeof decodedCookie.date === 'string' ? decodedCookie.date : null,
      dateRange: hasValidDateRange
        ? {
            startDate: decodedCookie.dateRange.startDate,
            endDate: decodedCookie.dateRange.endDate,
          }
        : null,
      guests: normalizeSearchFilterGuests(decodedCookie.guests),
    }
  } catch {
    return null
  }
}

const setSearchFiltersCookie = (value: ISearchFiltersCookieValue) => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie = cookie.serialize(SEARCH_FILTERS_COOKIE_KEY, JSON.stringify(value), {
    path: '/',
    maxAge: SEARCH_FILTERS_COOKIE_MAX_AGE,
  })
}

export const getSearchFiltersTotalGuestCount = (guests: ISelectedGuests) => {
  return guests.adults + guests.children + guests.infants
}

export const getDatesFromCookieDateRange = (
  dateRange: ISearchFiltersCookieValue['dateRange']
): moment.Moment[] => {
  if (!dateRange) {
    return []
  }

  const start = moment(dateRange.startDate, 'YYYY-MM-DD', true)
  const end = moment(dateRange.endDate, 'YYYY-MM-DD', true)

  if (!start.isValid() || !end.isValid()) {
    return []
  }

  const rangeStart = start.isAfter(end) ? end : start
  const rangeEnd = start.isAfter(end) ? start : end
  const diff = rangeEnd.diff(rangeStart, 'days')

  return Array.from({ length: diff + 1 }, (_, i) => moment(rangeStart).add(i, 'days'))
}

// 詳細ページの予約日付は検索条件から取得し、別ツアーの予約状態を持ち込まない。
export const getDatesFromSearchFilters = (
  filters: ISearchFiltersCookieValue | null
): moment.Moment[] => {
  const rangeDates = getDatesFromCookieDateRange(filters?.dateRange ?? null)
  if (rangeDates.length > 0) {
    return rangeDates
  }

  if (!filters?.date) {
    return []
  }

  const date = moment(filters.date, 'YYYY-MM-DD', true)
  return date.isValid() ? [date] : []
}

export const updateSearchFiltersCookie = (partial: Partial<ISearchFiltersCookieValue>): void => {
  const existingCookie = getSearchFiltersCookie()

  const newCookie: ISearchFiltersCookieValue = {
    areaFeature: null,
    date: null,
    dateRange: null,
    guests: DEFAULT_SEARCH_FILTER_GUESTS,
    ...existingCookie,
    ...partial,
  }

  newCookie.guests = normalizeSearchFilterGuests(newCookie.guests)

  setSearchFiltersCookie(newCookie)
}
